import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'

export interface CardProps {
  children: ReactNode
  className?: string
  /** Removes the border and ground, for a card that only groups content. */
  flush?: boolean
}

/** A bordered panel. One radius, one border, one hairline lift. */
export function Card({ children, className, flush }: CardProps) {
  return (
    <section
      className={cn(
        'rounded-lg',
        !flush && 'border border-line bg-surface-raised shadow-xs',
        className,
      )}
    >
      {children}
    </section>
  )
}

export interface CardHeaderProps {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  /** Rendered before the title — a status pill, a count, an icon. */
  meta?: ReactNode
  className?: string
}

export function CardHeader({
  title,
  description,
  actions,
  meta,
  className,
}: CardHeaderProps) {
  return (
    <header
      className={cn(
        'flex flex-wrap items-start justify-between gap-x-3 gap-y-2 border-b border-line px-4 py-3',
        className,
      )}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg leading-tight font-semibold tracking-[-0.01em] text-ink">
            {title}
          </h2>
          {meta}
        </div>
        {description ? (
          <p className="mt-1 text-xs text-ink-muted">{description}</p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
      ) : null}
    </header>
  )
}

export function CardBody({ children, className }: Omit<CardProps, 'flush'>) {
  return <div className={cn('p-4', className)}>{children}</div>
}

/**
 * A heading inside a card or a page section.
 *
 * Section titles were spelled four different ways across the app — `text-sm
 * font-semibold`, `text-xs uppercase`, `text-2xs uppercase`, and a bare `h3`.
 * One component, two deliberate levels.
 */
export function SectionHeader({
  title,
  description,
  actions,
  level = 'section',
  className,
}: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  /** `eyebrow` is the small uppercase label above a group of controls. */
  level?: 'section' | 'eyebrow'
  className?: string
}) {
  return (
    <div className={cn('flex items-end justify-between gap-3', className)}>
      <div className="min-w-0">
        <h3
          className={cn(
            level === 'eyebrow'
              ? 'text-2xs font-semibold tracking-[0.06em] text-ink-muted uppercase'
              : 'text-base font-semibold text-ink',
          )}
        >
          {title}
        </h3>
        {description ? (
          <p className="mt-0.5 text-xs text-ink-muted">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  )
}
